import { useMutation } from "@apollo/client";
import { gql } from "@/gql";
import type {
  CreateTeamMemberInput,
  UpdateTeamMemberInput,
  UserSalonRoleType,
} from "@/gql/graphql";
import { UPDATE_USER_SALON_ROLE_MUTATION } from "@/graphql/auth";
import { useAsyncAction } from "@/lib/hooks/use-async-action";
import { useSalonStore } from "@/lib/store/salon";

const CREATE_TEAM_MEMBER = gql(`
  mutation CreateTeamMember($salonId: ID!, $input: CreateTeamMemberInput!) {
    createTeamMember(salonId: $salonId, input: $input) {
      id
      workerId
      salonId
      franchiseId
      role
      color
      isActive
      canAcceptOnlineBookings
      createdAt
      updatedAt
      worker {
        id
        userId
        displayName
        bio
        profileImageUrl
        user {
          id
          email
          firstName
          lastName
        }
        invitations {
          ...InvitationData @unmask
        }
      }
    }
  }
`);

const UPDATE_TEAM_MEMBER = gql(`
  mutation UpdateTeamMember($salonId: ID!, $teamMemberId: ID!, $input: UpdateTeamMemberInput!) {
    updateTeamMember(salonId: $salonId, teamMemberId: $teamMemberId, input: $input) {
      id
      workerId
      salonId
      franchiseId
      role
      color
      isActive
      canAcceptOnlineBookings
      createdAt
      updatedAt
      worker {
        id
        userId
        displayName
        bio
        profileImageUrl
        user {
          id
          email
          firstName
          lastName
        }
        invitations {
          ...InvitationData @unmask
        }
      }
    }
  }
`);

const DELETE_TEAM_MEMBER = gql(`
  mutation DeleteTeamMember($salonId: ID!, $teamMemberId: ID!) {
    deleteTeamMember(salonId: $salonId, teamMemberId: $teamMemberId)
  }
`);

export function useCreateTeamMember() {
  const currentSalon = useSalonStore((state) => state.currentSalon);
  const [createTeamMemberMutation] = useMutation(CREATE_TEAM_MEMBER);

  return useAsyncAction(async (input: CreateTeamMemberInput) => {
    if (!currentSalon?.id) {
      throw new Error("No salon selected");
    }

    const result = await createTeamMemberMutation({
      variables: {
        salonId: currentSalon.id,
        input,
      },
      refetchQueries: ["TeamMembers"],
    });

    // Check for GraphQL errors
    if (result.errors && result.errors.length > 0) {
      throw new Error(result.errors[0].message);
    }

    if (!result.data?.createTeamMember) {
      throw new Error("Failed to create team member");
    }

    return result.data.createTeamMember;
  });
}

export function useUpdateTeamMember() {
  const currentSalon = useSalonStore((state) => state.currentSalon);
  const [updateTeamMemberMutation] = useMutation(UPDATE_TEAM_MEMBER);

  return useAsyncAction(async (input: UpdateTeamMemberInput & { id: string }) => {
    if (!currentSalon?.id) {
      throw new Error("No salon selected");
    }

    const { id, ...updateInput } = input;
    const result = await updateTeamMemberMutation({
      variables: {
        salonId: currentSalon.id,
        teamMemberId: id,
        input: updateInput,
      },
      refetchQueries: ["TeamMembers"],
    });

    // Check for GraphQL errors
    if (result.errors && result.errors.length > 0) {
      throw new Error(result.errors[0].message);
    }

    if (!result.data?.updateTeamMember) {
      throw new Error("Failed to update team member");
    }

    return result.data.updateTeamMember;
  });
}

export function useDeleteTeamMember() {
  const currentSalon = useSalonStore((state) => state.currentSalon);
  const [deleteTeamMemberMutation] = useMutation(DELETE_TEAM_MEMBER);

  return useAsyncAction(async (teamMemberId: string) => {
    if (!currentSalon?.id) {
      throw new Error("No salon selected");
    }

    const result = await deleteTeamMemberMutation({
      variables: {
        salonId: currentSalon.id,
        teamMemberId,
      },
      refetchQueries: ["TeamMembers"],
    });

    // Check for GraphQL errors
    if (result.errors && result.errors.length > 0) {
      throw new Error(result.errors[0].message);
    }

    return true;
  });
}

export function useAssignUserRole() {
  const currentSalon = useSalonStore((state) => state.currentSalon);
  const [updateUserSalonRoleMutation] = useMutation(UPDATE_USER_SALON_ROLE_MUTATION);

  return useAsyncAction(async (userId: string, roles: UserSalonRoleType[]) => {
    if (!currentSalon?.id) {
      throw new Error("No salon selected");
    }

    const result = await updateUserSalonRoleMutation({
      variables: {
        input: {
          userId,
          salonId: currentSalon.id,
          roles,
        },
      },
      refetchQueries: ["TeamMembers"],
    });

    // Check for GraphQL errors
    if (result.errors && result.errors.length > 0) {
      throw new Error(result.errors[0].message);
    }

    if (!result.data?.updateUserSalonRole) {
      throw new Error("Failed to update user role");
    }

    return result.data.updateUserSalonRole;
  });
}
